import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchTeamPerformance, listDailyStandups, listDepartments } from '../api';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const ROLE_LABELS = {
  PARTNER: 'Partner', ASSOCIATE: 'Associate', PARALEGAL: 'Paralegal',
  SUPPORT_STAFF: 'Support Staff', ACCOUNTANT: 'Accountant',
};

function TeamOverview({ authAxios, showFormMessage, userRole, currentUser }) {
  const [teamData, setTeamData] = useState(null);
  const [standups, setStandups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [period, setPeriod] = useState('30');

  const dateFrom = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - parseInt(period));
    return d.toISOString().slice(0, 10);
  }, [period]);
  const dateTo = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { date_after: dateFrom, date_before: dateTo };
      if (deptFilter) params.department = deptFilter;
      const [team, standupData, deptData] = await Promise.all([
        fetchTeamPerformance(authAxios, params).catch(() => null),
        listDailyStandups(authAxios, { ...params, ordering: '-date' }).catch(() => []),
        listDepartments(authAxios).catch(() => []),
      ]);
      setTeamData(team);
      setStandups(Array.isArray(standupData) ? standupData : []);
      setDepartments(Array.isArray(deptData) ? deptData : []);
    } catch { showFormMessage('error', 'Failed to load team data.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, dateFrom, dateTo, deptFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const employees = useMemo(() => {
    if (!teamData?.employees) return [];
    let list = [...teamData.employees];
    if (sortBy === 'name') list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortBy === 'hours') list.sort((a, b) => (b.total_hours || 0) - (a.total_hours || 0));
    else if (sortBy === 'billable') list.sort((a, b) => (b.billable_hours || 0) - (a.billable_hours || 0));
    else if (sortBy === 'utilization') {
      list.sort((a, b) => {
        const ua = a.total_hours ? (a.billable_hours / a.total_hours) : 0;
        const ub = b.total_hours ? (b.billable_hours / b.total_hours) : 0;
        return ub - ua;
      });
    }
    return list;
  }, [teamData, sortBy]);

  // Chart data
  const chartData = useMemo(() =>
    employees.slice(0, 15).map(e => ({
      name: (e.name || 'Unknown').split(' ').slice(0, 2).join(' '),
      billable: Math.round((e.billable_hours || 0) * 10) / 10,
      nonBillable: Math.round(((e.total_hours || 0) - (e.billable_hours || 0)) * 10) / 10,
    })), [employees]);

  // Alerts
  const alerts = useMemo(() => {
    const list = [];
    employees.forEach(e => {
      const util = e.total_hours ? (e.billable_hours / e.total_hours) * 100 : 0;
      if (util < 40 && e.total_hours > 0) {
        list.push({ type: 'warning', msg: `${e.name}: Low utilization (${util.toFixed(0)}%)` });
      }
    });
    // Standup compliance
    const unreviewedCount = standups.filter(s => !s.reviewed_at).length;
    if (unreviewedCount > 5) {
      list.push({ type: 'info', msg: `${unreviewedCount} standups pending review` });
    }
    return list;
  }, [employees, standups]);

  if (isLoading) return <div className="text-center py-12 text-gray-500">Loading team data...</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Team Overview</h2>
          <p className="text-sm text-gray-500 mt-1">Monitor team performance and productivity</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={period} onChange={e => setPeriod(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="name">Sort: Name</option>
            <option value="hours">Sort: Total Hours</option>
            <option value="billable">Sort: Billable Hours</option>
            <option value="utilization">Sort: Utilization</option>
          </select>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-4 space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className={`px-4 py-3 rounded-lg text-sm ${a.type === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
              {a.type === 'warning' ? '⚠️' : 'ℹ️'} {a.msg}
            </div>
          ))}
        </div>
      )}

      {/* Team Aggregate Stats */}
      {teamData && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-gray-800">{employees.length}</p>
            <p className="text-xs text-gray-500">Team Members</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-gray-800">
              {employees.reduce((s, e) => s + (e.total_hours || 0), 0).toFixed(0)}
            </p>
            <p className="text-xs text-gray-500">Total Hours</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {employees.reduce((s, e) => s + (e.billable_hours || 0), 0).toFixed(0)}
            </p>
            <p className="text-xs text-gray-500">Billable Hours</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {(() => {
                const th = employees.reduce((s, e) => s + (e.total_hours || 0), 0);
                const bh = employees.reduce((s, e) => s + (e.billable_hours || 0), 0);
                return th > 0 ? ((bh / th) * 100).toFixed(0) : 0;
              })()}%
            </p>
            <p className="text-xs text-gray-500">Avg Utilization</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{standups.length}</p>
            <p className="text-xs text-gray-500">Standups</p>
          </div>
        </div>
      )}

      {/* Team Hours Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Hours by Team Member</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Legend />
              <Bar dataKey="billable" stackId="a" fill="#22c55e" name="Billable" />
              <Bar dataKey="nonBillable" stackId="a" fill="#eab308" name="Non-Billable" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Employee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {employees.map(e => {
          const util = e.total_hours ? ((e.billable_hours / e.total_hours) * 100).toFixed(0) : 0;
          return (
            <div key={e.id || e.name} className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                  {(e.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{e.name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{ROLE_LABELS[e.role] || e.role} {e.department_name ? `• ${e.department_name}` : ''}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-gray-700">{(e.total_hours || 0).toFixed(1)}</p>
                  <p className="text-xs text-gray-400">Total</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-green-600">{(e.billable_hours || 0).toFixed(1)}</p>
                  <p className="text-xs text-gray-400">Billable</p>
                </div>
                <div>
                  <p className={`text-lg font-bold ${util >= 60 ? 'text-green-600' : util >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>{util}%</p>
                  <p className="text-xs text-gray-400">Util.</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
                <div className={`h-1.5 rounded-full ${util >= 60 ? 'bg-green-500' : util >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(util, 100)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>{e.standup_count || 0} standups</span>
                <span>{e.task_count || 0} tasks</span>
              </div>
            </div>
          );
        })}
      </div>

      {employees.length === 0 && !isLoading && (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
          No team data available for this period.
        </div>
      )}

      {/* Department Summary */}
      {teamData?.department_breakdown && Object.keys(teamData.department_breakdown).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Department Summary</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b">
                  <th className="pb-2 pr-4">Department</th>
                  <th className="pb-2 pr-4">Members</th>
                  <th className="pb-2 pr-4">Total Hours</th>
                  <th className="pb-2 pr-4">Billable</th>
                  <th className="pb-2">Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(teamData.department_breakdown).map(([dept, stats]) => {
                  const util = stats.total_hours ? ((stats.billable_hours / stats.total_hours) * 100).toFixed(0) : 0;
                  return (
                    <tr key={dept} className="hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium text-gray-700">{dept}</td>
                      <td className="py-3 pr-4 text-gray-600">{stats.member_count || '—'}</td>
                      <td className="py-3 pr-4 text-gray-600">{(stats.total_hours || 0).toFixed(1)}</td>
                      <td className="py-3 pr-4 text-green-600 font-medium">{(stats.billable_hours || 0).toFixed(1)}</td>
                      <td className="py-3">{util}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamOverview;
